import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
} from "@mui/material";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import BookmarkAddedRoundedIcon from "@mui/icons-material/BookmarkAddedRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProductSelectionModal from "../components/client/modal/ProductSelectionModal";
import api from "../services/api";
import { withCountryPrefix, withQuery } from "../components/utils/routeHelpers";

function toTs(value) {
  return value ? new Date(value).getTime() : 0;
}

function price(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "--";
}

function getCurrencySymbol(routeCountry, item) {
  return Number(item?.country_id) === 2 || String(routeCountry).toLowerCase() === "us" ? "$" : "€";
}

function humanizeDate(value, t) {
  if (!value) return t("no_recent_change");
  const diff = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(diff) || diff < 0) return new Date(value).toLocaleDateString();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return t("minutes_ago", { count: minutes || 1 });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("hours_ago", { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t("days_ago", { count: days });
  return new Date(value).toLocaleDateString();
}

function getChangeMeta(percent, t) {
  const value = Number(percent);
  if (!Number.isFinite(value) || value === 0) {
    return {
      color: "default",
      icon: <RemoveRoundedIcon sx={{ fontSize: 16 }} />,
      label: t("no_change"),
    };
  }
  if (value < 0) {
    return {
      color: "success",
      icon: <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} />,
      label: t("price_drop_percent", { percent: Math.abs(value).toFixed(2) }),
    };
  }
  return {
    color: "error",
    icon: <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />,
    label: t("price_increase_percent", { percent: value.toFixed(2) }),
  };
}

function getStoreLabel(item, fallback) {
  return [item?.store_chain, item?.store_name].filter(Boolean).join(" • ") || item?.store_label || fallback;
}

function getPackageText(item) {
  return (
    item?.package_size_text ||
    (item?.package_size_value && item?.package_size_unit
      ? `${item.package_size_value} ${item.package_size_unit}`
      : null)
  );
}

function formatUnitPrice(item, currencySymbol) {
  const value = Number(item?.unit_price);
  if (!Number.isFinite(value) || value <= 0) return null;
  const kind = String(item?.unit_price_kind || "").toLowerCase();
  if (kind === "piece") return `${currencySymbol}${value.toFixed(2)} / τεμ.`;
  if (kind === "liter" || kind === "l") return `${currencySymbol}${value.toFixed(2)} / L`;
  return `${currencySymbol}${value.toFixed(2)} / kg`;
}

function mergeWatchlistItems(items, nextItem, replacedItemId) {
  if (!nextItem?.id) {
    return replacedItemId ? items.filter((entry) => entry.id !== replacedItemId) : items;
  }
  const filtered = items.filter(
    (entry) =>
      entry.id !== replacedItemId &&
      entry.id !== nextItem.id &&
      entry.variant_id !== nextItem.variant_id
  );
  return [nextItem, ...filtered];
}

function watchIdentity(item) {
  const externalId = String(item?.external_id || "").trim().toLowerCase();
  if (externalId) return `external:${externalId}`;
  if (item?.product_id) return `product:${item.product_id}`;
  const fallback = [item?.name, item?.generic_product_name, item?.package_size_text, item?.store_chain]
    .map((part) => String(part || "").trim().toLowerCase())
    .filter(Boolean)
    .join("|");
  return fallback || `variant:${item?.variant_id || item?.id}`;
}

function groupWatchlistItems(rawItems) {
  const grouped = new Map();
  rawItems.forEach((item) => {
    const key = watchIdentity(item);
    if (!grouped.has(key)) {
      grouped.set(key, {
        ...item,
        duplicate_count: 1,
        duplicate_variant_ids: item?.variant_id ? [item.variant_id] : [],
      });
      return;
    }
    const current = grouped.get(key);
    const currentPrice = Number(current.price);
    const nextPrice = Number(item.price);
    const nextIsCheaper =
      Number.isFinite(nextPrice) &&
      (!Number.isFinite(currentPrice) || nextPrice < currentPrice);
    const merged = {
      ...(nextIsCheaper ? item : current),
      duplicate_count: Number(current.duplicate_count || 1) + 1,
      duplicate_variant_ids: [
        ...(current.duplicate_variant_ids || []),
        ...(item?.variant_id ? [item.variant_id] : []),
      ],
    };
    grouped.set(key, merged);
  });
  return Array.from(grouped.values());
}

export default function FollowedProductsPage() {
  const { t } = useTranslation();
  const { country = "el" } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [watchlistMeta, setWatchlistMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("recent_change");

  const [selectionOpen, setSelectionOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [genericProducts, setGenericProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedGenericId, setSelectedGenericId] = useState("");
  const [loadingGenericProducts, setLoadingGenericProducts] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [submittingSelection, setSubmittingSelection] = useState(false);
  const [selectionError, setSelectionError] = useState("");

  const plansPath = withQuery(withCountryPrefix("/plans", country), { intent: "premium", plan: "monthly" });
  const recipesPath = withCountryPrefix("/search-recipes", country);

  const loadItems = useCallback(() => {
    setLoading(true);
    return api.price
      .getWatchlist()
      .then((res) => {
        setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
        setWatchlistMeta(res?.data?.meta || {});
        setError("");
      })
      .catch((err) => {
        const status = err?.response?.status;
        setError(
          (status === 401
            ? t("login_required_title")
            : status === 403
              ? (err?.response?.data?.message || t("premium_feature_title"))
              : null) ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          t("followed_products_load_error")
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const groupedItems = useMemo(() => groupWatchlistItems(items), [items]);

  const sortedItems = useMemo(() => {
    const next = [...groupedItems];
    next.sort((a, b) => {
      if (sortBy === "recent_change") return toTs(b.price_changed_at) - toTs(a.price_changed_at);
      if (sortBy === "largest_drop") return Number(a.change_percent || a.delta_pct || 0) - Number(b.change_percent || b.delta_pct || 0);
      if (sortBy === "largest_increase") return Number(b.change_percent || b.delta_pct || 0) - Number(a.change_percent || a.delta_pct || 0);
      return String(a.generic_product_name || a.name || "").localeCompare(String(b.generic_product_name || b.name || ""));
    });
    return next;
  }, [groupedItems, sortBy]);

  const summary = useMemo(() => {
    const recent = groupedItems.filter((item) => {
      const ts = item?.price_changed_at ? new Date(item.price_changed_at).getTime() : 0;
      return ts > 0 && Date.now() - ts <= 7 * 24 * 60 * 60 * 1000;
    }).length;
    return {
      followed: groupedItems.length,
      recent,
    };
  }, [groupedItems]);

  const loadGenericProducts = useCallback(async (query = "") => {
    setLoadingGenericProducts(true);
    setSelectionError("");
    try {
      const res = await api.shoppingList.getSelectionGenericProducts({ q: query || undefined });
      const nextProducts = Array.isArray(res?.data?.items) ? res.data.items : [];
      setGenericProducts(nextProducts);
      return nextProducts;
    } catch (err) {
      setSelectionError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("generic_products_load_error")
      );
      return [];
    } finally {
      setLoadingGenericProducts(false);
    }
  }, [t]);

  const loadVariants = useCallback(async (genericProductId) => {
    if (!genericProductId) {
      setVariants([]);
      return [];
    }
    setLoadingVariants(true);
    setSelectionError("");
    try {
      const res = await api.shoppingList.getSelectionVariants(genericProductId);
      const nextVariants = Array.isArray(res?.data?.items) ? res.data.items : [];
      setVariants(nextVariants);
      return nextVariants;
    } catch (err) {
      setSelectionError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("variant_products_load_error")
      );
      setVariants([]);
      return [];
    } finally {
      setLoadingVariants(false);
    }
  }, [t]);

  const openSelectionModal = useCallback(async (item = null) => {
    setEditingItem(item);
    setSelectionOpen(true);
    setSearchValue("");
    setSelectionError("");
    const genericId = item?.generic_product_id ? String(item.generic_product_id) : "";
    setSelectedGenericId(genericId);
    await loadGenericProducts("");
    if (genericId) {
      await loadVariants(genericId);
    } else {
      setVariants([]);
    }
  }, [loadGenericProducts, loadVariants]);

  const closeSelectionModal = useCallback(() => {
    if (submittingSelection) return;
    setSelectionOpen(false);
    setEditingItem(null);
    setSearchValue("");
    setSelectedGenericId("");
    setVariants([]);
    setSelectionError("");
  }, [submittingSelection]);

  const handleSearchChange = useCallback(async (query) => {
    setSearchValue(query);
    await loadGenericProducts(query);
  }, [loadGenericProducts]);

  const handleSelectGeneric = useCallback(async (genericProductId) => {
    setSelectedGenericId(String(genericProductId));
    await loadVariants(genericProductId);
  }, [loadVariants]);

  const handleSelectVariant = useCallback(async (variantId) => {
    if (!variantId) return;
    setSubmittingSelection(true);
    setSelectionError("");
    const previousItems = items;
    const selectedVariant = variants.find(
      (entry) => String(entry?.id || entry?.product_variant_id) === String(variantId)
    );
    const selectedGeneric = genericProducts.find(
      (entry) => String(entry?.id) === String(selectedGenericId)
    );
    const optimisticItem = editingItem ? null : {
      id: `pending-${variantId}`,
      variant_id: variantId,
      generic_product_id: selectedGenericId ? Number(selectedGenericId) : null,
      generic_product_name:
        selectedGeneric?.name ||
        selectedGeneric?.name_el ||
        selectedGeneric?.name_en ||
        "",
      name:
        selectedVariant?.brand_name ||
        selectedVariant?.title ||
        selectedVariant?.name ||
        "",
      price: selectedVariant?.price ?? selectedVariant?.package_price ?? null,
      package_size_text: selectedVariant?.package_size_text || null,
      store_name: selectedVariant?.store_name || null,
      store_chain: selectedVariant?.store_chain || null,
      unit_price: selectedVariant?.unit_price ?? null,
      unit_price_kind: selectedVariant?.unit_price_kind ?? null,
      price_changed_at: null,
      price_was: null,
      change_percent: null,
      delta_pct: null,
    };
    try {
      if (optimisticItem) {
        setItems((current) => mergeWatchlistItems(current, optimisticItem, null));
      }
      if (editingItem?.variant_id && String(editingItem.variant_id) !== String(variantId)) {
        await api.price.updateWatchlist({
          variant_id: editingItem.variant_id,
          action: "remove",
        });
      }

      const res = await api.price.updateWatchlist({ variant_id: variantId });
      const updatedItem = res?.data?.item || null;
      setItems((current) => mergeWatchlistItems(current, updatedItem, editingItem?.id));
      closeSelectionModal();
      await loadItems();
    } catch (err) {
      setItems(previousItems);
      setSelectionError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("watchlist_add_error")
      );
    } finally {
      setSubmittingSelection(false);
    }
  }, [closeSelectionModal, editingItem, genericProducts, items, loadItems, selectedGenericId, t, variants]);

  const handleUnfollow = useCallback(async (item) => {
    const previous = items;
    setItems((current) => current.filter((entry) => entry.id !== item.id));
    try {
      const variantIds = Array.from(new Set(item.duplicate_variant_ids?.length ? item.duplicate_variant_ids : [item.variant_id]));
      await Promise.all(
        variantIds
          .filter(Boolean)
          .map((variantId) => api.price.updateWatchlist({
            variant_id: variantId,
            action: "remove",
          }))
      );
      await loadItems();
    } catch (err) {
      setItems(previous);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("followed_products_remove_error") ||
        "Unable to remove followed product."
      );
    }
  }, [items, loadItems, t]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2.5}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            background: (theme) => theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(73,188,144,0.12), rgba(20,22,26,0.95))"
              : "linear-gradient(135deg, rgba(0,158,117,0.08), rgba(255,255,255,0.96))",
            boxShadow: (theme) => theme.palette.mode === "dark"
              ? "0 16px 42px rgba(0,0,0,0.32)"
              : "0 16px 38px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: { xs: "flex-start", md: "center" }, flexDirection: { xs: "column", md: "row" } }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <BookmarkAddedRoundedIcon color="success" />
                    <Chip size="small" color="warning" variant="outlined" label={t("premium_required")} />
                  </Stack>
                  <Typography variant="h4" fontWeight={800}>
                    {t("price_watch_title")}
                  </Typography>
                  <Typography color="text.secondary">
                    {t("price_watch_subtitle")}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip color="success" variant="filled" label={`${summary.followed} ${t("followed_items")}`} />
                  <Chip color={summary.recent > 0 ? "warning" : "default"} variant="outlined" label={`${summary.recent} ${t("recent_changes")}`} />
                  {watchlistMeta?.limit != null ? (
                    <Chip variant="outlined" label={`${watchlistMeta.count || 0}/${watchlistMeta.limit}`} />
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: { xs: "flex-start", md: "center" }, flexDirection: { xs: "column", md: "row" } }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {t("price_watch_title")}
            </Typography>
            <Typography color="text.secondary">
              {t("price_watch_sort_hint")}
            </Typography>
          </Box>
          <TextField
            select
            size="small"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="recent_change">{t("sort_recent_change")}</MenuItem>
            <MenuItem value="largest_drop">{t("sort_largest_drop")}</MenuItem>
            <MenuItem value="largest_increase">{t("sort_largest_increase")}</MenuItem>
            <MenuItem value="alphabetical">{t("sort_alphabetical")}</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={() => openSelectionModal()}
            sx={{
              borderRadius: 999,
              px: 2.25,
              py: 1,
              fontWeight: 800,
              boxShadow: (theme) => theme.palette.mode === "dark"
                ? "0 10px 24px rgba(73,188,144,0.25)"
                : "0 10px 22px rgba(0,158,117,0.18)",
            }}
          >
            {t("add_watch_button")}
          </Button>
        </Box>

        {error ? (
          <Alert
            severity="warning"
            action={
              error.toLowerCase().includes("premium") ? (
                <Button color="inherit" size="small" onClick={() => navigate(plansPath)}>
                  {t("upgrade_to_premium")}
                </Button>
              ) : (
                <Button color="inherit" size="small" onClick={loadItems}>
                  {t("retry")}
                </Button>
              )
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Stack spacing={2}>
            {[0, 1, 2].map((idx) => (
              <Card key={idx} variant="outlined" sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Stack spacing={1.2}>
                    <Skeleton variant="text" width="38%" height={36} />
                    <Skeleton variant="text" width="28%" />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Skeleton variant="rounded" width={120} height={28} />
                      <Skeleton variant="rounded" width={120} height={28} />
                      <Skeleton variant="rounded" width={130} height={28} />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : sortedItems.length === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 4 }}>
            <CardContent>
              <Stack spacing={1.5} alignItems="flex-start">
                <Chip color="default" variant="outlined" label={t("empty_state")} />
                <Typography variant="h6" fontWeight={800}>
                  {t("followed_products_empty_title")}
                </Typography>
                <Typography color="text.secondary">
                  {t("followed_products_empty")}
                </Typography>
                <Button variant="contained" onClick={() => navigate(recipesPath)}>
                  {t("browse_recipes")}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {sortedItems.map((item) => {
              const currencySymbol = getCurrencySymbol(country, item);
              const packageText = getPackageText(item);
              const unitPriceText = formatUnitPrice(item, currencySymbol);
              const changeMeta = getChangeMeta(item.change_percent ?? item.delta_pct, t);
              const title = item.name || item.generic_product_name || `Product #${item.variant_id}`;
              const genericTitle = item.generic_product_name && item.generic_product_name !== title ? item.generic_product_name : null;
              const fullName = [title, genericTitle].filter(Boolean).join(" · ");
              return (
                <Card
                  key={item.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    boxShadow: (theme) => theme.palette.mode === "dark"
                      ? "0 10px 24px rgba(0,0,0,0.28)"
                      : "0 8px 22px rgba(0,0,0,0.06)",
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.2}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                        <Box>
                          <Tooltip title={fullName} enterDelay={450}>
                            <Typography
                              fontWeight={800}
                              sx={{
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                display: "-webkit-box",
                                WebkitLineClamp: { xs: 3, sm: 2 },
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {title}
                            </Typography>
                          </Tooltip>
                          {genericTitle ? (
                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                              {genericTitle}
                            </Typography>
                          ) : null}
                          <Typography variant="body2" color="text.secondary">
                            <strong>{t("best_offer_label")}:</strong> {getStoreLabel(item, t("store_unavailable"))}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" color={changeMeta.color} icon={changeMeta.icon} label={changeMeta.label} />
                          <Button size="small" variant="outlined" onClick={() => openSelectionModal(item)}>
                            {t("change_watch_product")}
                          </Button>
                          <Button size="small" color="error" onClick={() => handleUnfollow(item)}>
                            {t("unfollow_price")}
                          </Button>
                        </Stack>
                      </Box>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
                        <Typography variant="body2"><strong>{t("current_price")}:</strong> {price(item.price)}{currencySymbol}</Typography>
                        {packageText ? (
                          <Typography variant="body2"><strong>{t("package_size")}:</strong> {packageText}</Typography>
                        ) : null}
                        {unitPriceText ? (
                          <Typography variant="body2"><strong>{t("unit_price")}:</strong> {unitPriceText}</Typography>
                        ) : null}
                        {Number(item.duplicate_count || 0) > 1 ? (
                          <Typography variant="body2" color="text.secondary">
                            <strong>{t("grouped_offers_label")}:</strong> {t("grouped_nearby_offers", { count: item.duplicate_count })}
                          </Typography>
                        ) : null}
                        <Typography variant="body2"><strong>{t("price_was")}:</strong> {price(item.price_was)}{currencySymbol}</Typography>
                        <Typography variant="body2"><strong>{t("last_change")}:</strong> {humanizeDate(item.price_changed_at, t)}</Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>

      <ProductSelectionModal
        open={selectionOpen}
        onClose={closeSelectionModal}
        genericProducts={genericProducts}
        variants={variants}
        selectedGenericId={selectedGenericId}
        currentVariantId={editingItem?.variant_id || ""}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSelectGeneric={handleSelectGeneric}
        onSelectVariant={handleSelectVariant}
        loadingGenericProducts={loadingGenericProducts}
        loadingVariants={loadingVariants}
        submittingSelection={submittingSelection}
        error={selectionError}
        title={editingItem ? t("change_watch_product") : t("add_watch_button")}
        emptyProductsLabel={t("no_generic_products_found")}
        emptyVariantsLabel={t("no_variants_found")}
      />
    </Container>
  );
}
